import { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { FiMinus, FiTrendingDown, FiTrendingUp } from 'react-icons/fi';
import styles from '../../styles/Card.module.css';
import arrowRight from '../../assets/icons/rightarrow.svg';

echarts.use([LineChart, GridComponent, CanvasRenderer]);

/** Same left→right rise when series trends down (color still reflects real trend). */
function toUpwardSparkDisplay(data) {
  const n = data.length;
  if (n < 2) return data;
  const minV = Math.min(...data);
  const maxV = Math.max(...data);
  if (maxV === minV) {
    const v = minV;
    const base = v === 0 ? 1 : Math.max(1, Math.abs(v) * 0.08);
    return data.map((_, i) => {
      const t = n === 1 ? 0 : i / (n - 1);
      return Math.max(0, v - base + 2 * base * t);
    });
  }
  return data.map((_, i) => {
    const t = n === 1 ? 0 : i / (n - 1);
    return minV + (maxV - minV) * t;
  });
}

function formatTotalForPill(v) {
  if (v == null) return '';
  if (typeof v === 'number' && Number.isFinite(v)) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(v);
  }
  return String(v);
}

/**
 * Card: title + optional trend pill (total value), main value, optional spark (ECharts) on the right,
 * footer = subtitle + arrow.
 */
export default function Card({
  title,
  value,
  topRight,
  bottomText,
  graphData,
  graphAriaLabel = 'Trend',
  previousValue,
}) {
  const [lineColorError, setLineColorError] = useState('#e31a1a');
  const [lineColorSuccess, setLineColorSuccess] = useState('#01b574');

  const series = useMemo(
    () => (Array.isArray(graphData) ? graphData.filter((n) => Number.isFinite(n)) : []),
    [graphData]
  );
  const trendIndex = series.length ? series.length - 1 : null;
  const trendDiffSequential =
    trendIndex != null && trendIndex > 0
      ? (series[trendIndex] ?? 0) - (series[trendIndex - 1] ?? 0)
      : 0;
  const isTrendDown = trendDiffSequential < 0;
  const isFlatOfficers = title === 'Field Officers' && series.length > 0 && series.every((v) => v === series[0]);

  const echartsDisplaySeries = useMemo(() => {
    if (series.length < 2) return series;
    if (isFlatOfficers) return series;
    if (isTrendDown) return toUpwardSparkDisplay(series);
    return series;
  }, [series, isTrendDown, isFlatOfficers]);

  const strokeColor = useMemo(() => {
    if (isFlatOfficers) return lineColorSuccess;
    return isTrendDown ? lineColorError : lineColorSuccess;
  }, [isFlatOfficers, isTrendDown, lineColorError, lineColorSuccess]);

  const sparkEchartsOption = useMemo(() => {
    if (echartsDisplaySeries.length < 2) return null;
    return {
      animation: false,
      grid: { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },
      xAxis: {
        type: 'category',
        show: false,
        boundaryGap: false,
        data: echartsDisplaySeries.map((_, i) => i),
      },
      yAxis: { type: 'value', show: false, scale: true },
      series: [
        {
          type: 'line',
          data: echartsDisplaySeries,
          showSymbol: false,
          smooth: true,
          lineStyle: { width: 2.5, color: strokeColor },
          areaStyle: { color: strokeColor, opacity: 0.12 },
        },
      ],
    };
  }, [echartsDisplaySeries, strokeColor]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const r = document.documentElement;
    const s = getComputedStyle(r);
    setLineColorError(s.getPropertyValue('--text-error-primary').trim() || '#e31a1a');
    setLineColorSuccess(s.getPropertyValue('--text-success-primary').trim() || '#01b574');
  }, []);

  const showSpark = Boolean(sparkEchartsOption);
  const showPill = showSpark;
  const pillIsNeutral = isFlatOfficers;
  const pillIsDown = !pillIsNeutral && isTrendDown;

  /** Main headline total (same as large value) — shown in the pill. */
  const pillTotalText = formatTotalForPill(value);

  const headerRight = showPill ? (
    <div
      className={[
        styles.trendPill,
        pillIsNeutral
          ? styles.trendPillNeutral
          : pillIsDown
            ? styles.trendPillError
            : styles.trendPillSuccess,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {pillIsNeutral ? (
        <FiMinus className={styles.trendIcon} aria-hidden />
      ) : pillIsDown ? (
        <FiTrendingDown className={styles.trendIcon} aria-hidden />
      ) : (
        <FiTrendingUp className={styles.trendIcon} aria-hidden />
      )}
      <span className={styles.trendPillText}>{pillTotalText}</span>
    </div>
  ) : topRight ? (
    <div className={styles.topRight}>{topRight}</div>
  ) : null;

  return (
    <div className={styles.root}>
      <div className={styles.frame}>
        <div className={styles.cardBody}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>{title}</h3>
            {headerRight}
          </div>

          <div className={styles.valueRow}>
            <p className={styles.value}>{value}</p>
            {showSpark ? (
              <div className={styles.sparkWrap} aria-label={graphAriaLabel}>
                <ReactECharts
                  echarts={echarts}
                  option={sparkEchartsOption}
                  style={{ width: 132, height: 44 }}
                  opts={{ renderer: 'canvas' }}
                  aria-hidden
                />
              </div>
            ) : null}
          </div>

          {previousValue ? <p className={styles.previousValue}>{previousValue}</p> : null}
        </div>

        <footer className={styles.footer}>
          {bottomText ? (
            <p className={styles.footerText}>{bottomText}</p>
          ) : (
            <span className={styles.footerPlaceholder} aria-hidden="true" />
          )}
          <img className={styles.footerArrow} src={arrowRight} alt="" aria-hidden="true" />
        </footer>
      </div>
    </div>
  );
}
